import { useFormContext } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

const NAME_MAX = 256
const DESCRIPTION_MAX = 3000

interface ProjectBasicInfoSectionProps {
  mode?: 'create' | 'edit'
}

export function ProjectBasicInfoSection({ mode = 'create' }: ProjectBasicInfoSectionProps) {
  const { register, watch, formState: { errors } } = useFormContext()
  const nameValue = watch('name') ?? ''
  const descriptionValue = watch('description') ?? ''

  const isEditMode = mode === 'edit'

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {isEditMode ? (
          <>
            <Label htmlFor="name">Project Title <span className="text-red-500">*</span></Label>
            <Input
              id="name"
              value={nameValue}
              disabled
              className="bg-muted text-muted-foreground cursor-not-allowed"
            />
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <Label htmlFor="name">Title <span className="text-red-500">*</span></Label>
              <span className="text-xs text-muted-foreground">{nameValue.trim().length}/{NAME_MAX}</span>
            </div>
            <Input
              id="name"
              placeholder="Enter project title"
              {...register('name', {
                required: 'Project title is required',
                validate: (value) => {
                  const trimmed = value.trim()
                  if (trimmed.length === 0) return 'Project title is required'
                  if (trimmed.length > NAME_MAX) return `Title must be at most ${NAME_MAX} characters`
                  return true
                },
              })}
            />
            {errors.name && (
              <p role="alert" className="text-sm text-destructive">{String(errors.name.message)}</p>
            )}
          </>
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="description">Description</Label>
          <span className="text-xs text-muted-foreground">{descriptionValue.trim().length}/{DESCRIPTION_MAX}</span>
        </div>
        <Textarea
          id="description"
          placeholder="Enter project description"
          rows={4}
          {...register('description', {
            validate: (value) => {
              if (value && value.trim().length > DESCRIPTION_MAX) return `Description must be at most ${DESCRIPTION_MAX} characters`
              return true
            },
          })}
        />
        {errors.description && (
          <p role="alert" className="text-sm text-destructive">{String(errors.description.message)}</p>
        )}
      </div>
    </div>
  )
}
